/* eslint-disable camelcase */
import { pickBy } from 'lodash';

import { gaBridge } from 'client/utils/event-bridge';
import { appLog } from 'utils/logger';
import { location as UrlHelperLocation } from 'utils/url-helper';
import { parseGAParams } from 'utils/ga-params';
import { resolveCustomTrackerVariables } from 'utils/resolve-custom-tracker-variables';

export type GAEvent = {
	type: string;
	action: string;
	payload: { [key: string]: string | number | boolean };
};

/** Unified Media (VIDEO) block: GA action prefix and category. */
export type MediaAnalyticsKind = 'video' | 'audio';

export function mediaAnalyticsLabels(kind: MediaAnalyticsKind) {
	if (kind === 'audio') {
		return { noun: 'audio' as const, eventCategory: 'Audio interaction' as const };
	}
	return { noun: 'video' as const, eventCategory: 'Video interaction' as const };
}

type ValidRecord<T> = {
	[K in keyof T]: T[K] extends string | number | boolean ? T[K] : never;
};

const { isPreview } = UrlHelperLocation.client;
const log = appLog.extend('utils:analytics');

const pickValid = <T extends Record<string, any>>(o: T) =>
	pickBy(o, v => v !== null && v !== undefined && v !== '') as ValidRecord<T>;

/**
 */
/**
 * Note: Update EVENTS.md when adding, editing, or deleting API events
 *
 * @info https://developers.google.com/analytics/devguides/collection/gtagjs/events
 * 		 https://developers.google.com/tag-platform/gtagjs/reference/parameters
 */
async function track(eventName: string, { type, action, payload }: GAEvent) {
	try {
		if (isPreview) {
			return;
		}

		if (!window.gtag) {
			console.warn("gtag is't initialized");
			return;
		}

		const params = pickValid(payload);
		log('gtag', type, action, params);
		window.gtag(type, action, params);
		await gaBridge.sendEvent({ type: eventName, details: { type, action, payload: params } });
	} catch (error) {
		console.warn('Failed to track analytics event', error);
	}
}

export const analytics = {
	onLinkClick: (params: { storyId: string; cardId: string; name: string }) =>
		track('onLinkClick', {
			type: 'event',
			action: `link click story ${params.storyId} | card ${params.cardId} | ${params.name}`,
			payload: {
				event_category: 'Link click',
				layerName: params.name,
				storyId: params.storyId,
				cardId: params.cardId,
			},
		}),
	onApiRetry: (attemptNumber, apiRequestName) =>
		track('onApiRetry', {
			type: 'event',
			action: `Retry ${attemptNumber} - ${apiRequestName}`,
			payload: {
				event_category: 'Retry to api',
				event_label: `${apiRequestName}`,
			},
		}),
	onApiError: (errorCode, apiRequestName) =>
		track('onApiError', {
			type: 'event',
			action: `${apiRequestName}`,
			payload: {
				event_category: 'Error from api',
				event_label: `${errorCode} code`,
			},
		}),
	onOffline: (storyId, cardId, requestRoute) =>
		track('onOffline', {
			type: 'event',
			action: `offline mode storyid ${storyId} card ${cardId}`,
			payload: {
				event_category: 'offline mode no session',
				event_label: `${requestRoute}`,
				send_to: window.STORYCARDS_GA_ID,
			},
		}),
	onSendAnswer: (storyId: string, cardId: string, answerId: string, answerText: string) =>
		track('onSendAnswer', {
			type: 'event',
			action: `Send Answer story ${storyId} card ${cardId} `,
			payload: {
				event_category: 'Send answer',
				event_label: `answer ${answerId} ${answerText}`,
			},
		}),
	onSearchPerformed: (params: { storyId: string; cardId: string; layerName: string }) =>
		track('onSearchPerformed', {
			type: 'event',
			action: `search_performed | story ${params.storyId} | card ${params.cardId} | name ${params.layerName}`,
			payload: {
				event_category: 'Search performed',
				storyId: params.storyId,
				cardId: params.cardId,
				layerName: params.layerName,
			},
		}),
	onSearchQuery: (params: { storyId: string; cardId: string; layerName: string; searchTerm: string }) =>
		track('onSearchQuery', {
			type: 'event',
			// eslint-disable-next-line max-len
			action: `search_query_entered | story ${params.storyId} | card ${params.cardId} | ${params.layerName} | search_term ${params.searchTerm}`,
			payload: {
				event_category: 'Search query entered',
				storyId: params.storyId,
				cardId: params.cardId,
				layerName: params.layerName,
				searchTerm: params.searchTerm,
			},
		}),
	onVideoPlay: (params: {
		storyId: string;
		cardId: string;
		layerName: string;
		mediaUrl: string;
		mediaDuration: number;
		mediaContentId?: string;
		mediaKind?: MediaAnalyticsKind;
	}) => {
		const kind = params.mediaKind ?? 'video';
		const { noun, eventCategory } = mediaAnalyticsLabels(kind);
		const eventName = kind === 'audio' ? 'onAudioPlay' : 'onVideoPlay';
		return track(eventName, {
			type: 'event',
			action: `${noun} play story ${params.storyId} | card ${params.cardId} | ${params.layerName}`,
			payload: pickValid({
				event_category: eventCategory,
				storyId: params.storyId,
				cardId: params.cardId,
				layerName: params.layerName,
				[`${noun}Url`]: params.mediaUrl,
				[`${noun}Duration`]: params.mediaDuration,
				[`${noun}ContentId`]: params.mediaContentId,
			}),
		});
	},
	onVideoProgress: (params: {
		storyId: string;
		cardId: string;
		layerName: string;
		mediaUrl: string;
		mediaDuration: number;
		mediaProgress: number;
		mediaContentId?: string;
		mediaKind?: MediaAnalyticsKind;
	}) => {
		const kind = params.mediaKind ?? 'video';
		const { noun, eventCategory } = mediaAnalyticsLabels(kind);
		const eventName = kind === 'audio' ? 'onAudioProgress' : 'onVideoProgress';
		return track(eventName, {
			type: 'event',
			action: `${noun} progress story ${params.storyId} | card ${params.cardId} | ${params.layerName}`,
			payload: pickValid({
				event_category: eventCategory,
				storyId: params.storyId,
				cardId: params.cardId,
				layerName: params.layerName,
				[`${noun}Url`]: params.mediaUrl,
				[`${noun}Duration`]: params.mediaDuration,
				[`${noun}Progress`]: params.mediaProgress,
				[`${noun}ContentId`]: params.mediaContentId,
			}),
		});
	},
	onVideoComplete: (params: {
		storyId: string;
		cardId: string;
		layerName: string;
		mediaUrl: string;
		mediaDuration: number;
		mediaContentId?: string;
		mediaKind?: MediaAnalyticsKind;
	}) => {
		const kind = params.mediaKind ?? 'video';
		const { noun, eventCategory } = mediaAnalyticsLabels(kind);
		const eventName = kind === 'audio' ? 'onAudioComplete' : 'onVideoComplete';
		return track(eventName, {
			type: 'event',
			action: `${noun} complete story ${params.storyId} | card ${params.cardId} | ${params.layerName}`,
			payload: pickValid({
				event_category: eventCategory,
				storyId: params.storyId,
				cardId: params.cardId,
				layerName: params.layerName,
				[`${noun}Url`]: params.mediaUrl,
				[`${noun}Duration`]: params.mediaDuration,
				[`${noun}ContentId`]: params.mediaContentId,
			}),
		});
	},
	onMediaEngaged3s: (params: {
		storyId: string;
		cardId: string;
		layerName: string;
		mediaUrl: string;
		mediaDuration: number;
		mediaContentId?: string;
		mediaKind?: MediaAnalyticsKind;
	}) => {
		const kind = params.mediaKind ?? 'video';
		const { noun, eventCategory } = mediaAnalyticsLabels(kind);
		const eventName = kind === 'audio' ? 'onAudioEngaged3s' : 'onVideoEngaged3s';

		return track(eventName, {
			type: 'event',
			action: `${noun} engaged 3s ${params.storyId} | ${params.cardId} | ${params.layerName}`,
			payload: pickValid({
				event_category: eventCategory,
				storyId: params.storyId,
				cardId: params.cardId,
				layerName: params.layerName,
				[`${noun}Url`]: params.mediaUrl,
				[`${noun}Duration`]: params.mediaDuration,
				[`${noun}ContentId`]: params.mediaContentId,
			}),
		});
	},
	onCustomEvent: ({
		gaParamsStr,
		storyName,
		cardName,
		linkUrl,
	}: {
		gaParamsStr: string;
		storyName?: string;
		cardName?: string;
		linkUrl?: string;
	}) => {
		const gaParams = parseGAParams(gaParamsStr);
		const context = {
			storyName: storyName ?? '',
			cardName: cardName ?? '',
			linkUrl: linkUrl ?? '',
		};

		if (!gaParams.actionName) {
			return;
		}

		// Resolve placeholders (CUSTOM_TRACKER_OPTIONS) in each parameter value
		const resolvedPayload = gaParams.params.reduce(
			(acc, param) => {
				acc[param.name] = resolveCustomTrackerVariables(param.value, context);
				return acc;
			},
			{} as Record<string, string>
		);

		track('onCustomEvent', {
			type: 'event',
			action: gaParams.actionName,
			payload: resolvedPayload,
		});
	},
};

let isFirstPageView = true;

/**
 * @param props.location The page's URL.
 * @param props.path The path portion of location. This value must start with a slash (/) character.
 * @param props.title The page's title.
 * @param props.send_to GA_MEASUREMENT_ID
 * @info https://developers.google.com/analytics/devguides/collection/gtagjs/pages
 */
export const pageview = async (props: { location: string; path: string; title: string; gaUniversalId?: string }) => {
	if (isPreview || !window.gtag) return;

	const { gaUniversalId, ...validParams } = pickValid({
		page_path: props.path,
		page_title: props.title,
		page_location: props.location,
		gaUniversalId: props.gaUniversalId,
	});

	// Setting properties globally to ensure GA uses these values consistently.
	// This prevents GA from using properties from a previous event if not provided.
	if (validParams.page_path) window.gtag('set', 'page_path', validParams.page_path);
	if (validParams.page_title) window.gtag('set', 'page_title', validParams.page_title);
	if (validParams.page_location) window.gtag('set', 'page_location', validParams.page_location);

	log('pageview', validParams);

	if (isFirstPageView) {
		// Initial 'page_view' event doesn't send automatically by default (see `{ send_page_view: false }`)
		await trackPageView({ type: 'event', action: 'page_view', payload: validParams });
		isFirstPageView = false;
	} else if (gaUniversalId) {
		// GA4 sends 'page_view' events automatically by default, while GA3 (Universal) requires manual sending.
		await trackPageView({
			type: 'event',
			action: 'page_view',
			payload: { ...validParams, send_to: gaUniversalId },
		});
	}

	async function trackPageView(params: GAEvent) {
		if (!window.gtag) {
			return;
		}

		window.gtag(params.type, params.action, params.payload);
		await gaBridge.sendEvent({ type: 'page_view', details: params });
	}
};
